import { Save, FolderOpen, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';

interface SessionStepProps {
  onSaveSession?: () => void;
}

export function SessionStep({ onSaveSession }: SessionStepProps) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">Manage Your Sessions</h2>
        <p className="text-gray-600">
          Save your work to return to it later
        </p>
      </div>

      <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
            <FolderOpen className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">What are sessions?</h3>
            <p className="text-sm text-gray-600">
              Sessions save your documents, conversations, and analysis
            </p>
          </div>
        </div>

        <div className="space-y-3 pl-1">
          <div className="flex gap-3 items-start">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-900">Keep your work organized</p>
              <p className="text-xs text-gray-600">
                Create separate sessions for different cases, projects, or research topics
              </p>
            </div>
          </div>

          <div className="flex gap-3 items-start">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-900">Return to your analysis</p>
              <p className="text-xs text-gray-600">
                All your documents and conversation history are saved and ready when you come back
              </p>
            </div>
          </div>

          <div className="flex gap-3 items-start">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-900">Organize in folders</p>
              <p className="text-xs text-gray-600">
                Create folders to group related sessions together for easy access
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Save className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">When to save</h3>
          </div>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex gap-2">
              <span className="text-blue-600">•</span>
              <span>After adding important documents</span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-600">•</span>
              <span>When you have useful conversations</span>
            </li>
            <li className="flex gap-2">
              <span className="text-blue-600">•</span>
              <span>Before switching to a new project</span>
            </li>
          </ul>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-gray-900">How to save</h3>
          </div>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex gap-2">
              <span className="text-purple-600">1.</span>
              <span>Click the Save button in the header</span>
            </li>
            <li className="flex gap-2">
              <span className="text-purple-600">2.</span>
              <span>Give your session a name</span>
            </li>
            <li className="flex gap-2">
              <span className="text-purple-600">3.</span>
              <span>Choose a folder (optional)</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center space-y-3">
        <p className="text-sm text-gray-700">
          <strong>Don't worry!</strong> You can save your session anytime. For now, you can explore
          and save when you're ready.
        </p>
        {onSaveSession && (
          <Button onClick={onSaveSession} variant="outline" size="sm" className="gap-2">
            <Save className="w-4 h-4" />
            Save Session Now
          </Button>
        )}
      </div>
    </div>
  );
}
